import { useCallback, useMemo, useState } from 'react';
import { Select } from 'antd';
import { BordTooltip } from 'bord-design-system';
import { SearchIcon } from 'assets/images/countryList';
import { TAny } from 'types/blog';
import { bordColorTheme, nudosGray100, nudosGray300, nudosTextcolor } from 'styles/styles.constants';

import './countryProviderCell.scss';

interface IProviderOption {
  value: string;
  label: string;
}

interface ICountryProviderCell {
  countryId?: number;
  countryCode?: string;
  countryProviders?: string[];
  hasProviderValue: boolean;
  onAddProvider?: (provider: string, countryCode: string, countryId: number) => void;
}

const NEW_COUNTRY_PROVIDER_TOOLTIP_MESSAGE =
  'Primero debes terminar de crear el país para luego elegir los proveedores';

/**
 * Celda de búsqueda y selección de proveedores para una fila de país.
 */
const CountryProviderCell = ({
  countryId,
  countryCode,
  countryProviders = [],
  hasProviderValue,
  onAddProvider
}: ICountryProviderCell) => {
  const [searchString, setSearchString] = useState<string>('');

  const providerOptions = useMemo<IProviderOption[]>(
    () =>
      countryProviders.map(provider => ({
        label: provider,
        value: provider
      })),
    [countryProviders]
  );

  const isProviderSelectDisabled = !countryId;
  const providersDisplayValue = useMemo(() => {
    if (countryProviders.length === 0) return undefined;

    return countryProviders.toString().replaceAll(',', ', ');
  }, [countryProviders]);

  const searchIconStroke = hasProviderValue ? nudosTextcolor : nudosGray300;
  const searchIconFill = isProviderSelectDisabled ? nudosGray100 : bordColorTheme.white.default;

  /**
   * Registra un proveedor nuevo a partir del texto buscado.
   */
  const handleAddNewProvider = useCallback(() => {
    if (!onAddProvider || !countryId || !countryCode || !searchString) return;

    onAddProvider(searchString, countryCode, countryId);
  }, [onAddProvider, countryId, countryCode, searchString]);

  const noFoundContent = useMemo(() => {
    if (!searchString) return null;

    const searchLabel = searchString.length > 15 ? `${searchString.substring(0, 15)}...` : searchString;

    return (
      <button type="button" className="newProvider" onClick={handleAddNewProvider}>
        {`${searchLabel} - `}
        <span className="newProviderAction">Nuevo</span>
      </button>
    );
  }, [searchString, handleAddNewProvider]);

  return (
    <div className="countryProviderCell">
      <div className="containerProviderInput">
        <label className={`labelCountryInput ${hasProviderValue ? 'filledLabel' : ''}`}>Proveedor</label>

        <div className="countryRowInputZone countryRowInputZoneStart">
          <div className={`selectProviders customBoder ${hasProviderValue ? 'filledInput' : ''}`}>
            {isProviderSelectDisabled && (
              <BordTooltip
                followMouse
                position="bottom"
                message={NEW_COUNTRY_PROVIDER_TOOLTIP_MESSAGE}
                customClassName="countryProviderCellTooltipTrigger"
                customClassNameMessageContainer="countryProviderCellTooltip"
                messageContainerSize={{ width: '16.9rem', minWidth: '16.9rem', maxWidth: '16.9rem' }}
              >
                <div className="disabledOverlay" aria-hidden />
              </BordTooltip>
            )}

            <div className="floatIcon">
              <SearchIcon stroke={searchIconStroke} fill={searchIconFill} />
            </div>

            <Select
              defaultValue={providersDisplayValue}
              className="customSelect"
              showArrow={false}
              showSearch
              disabled={isProviderSelectDisabled}
              placeholder="Buscar"
              optionFilterProp="children"
              getPopupContainer={triggerNode => triggerNode.parentElement ?? document.body}
              onSearch={value => setSearchString(value)}
              notFoundContent={noFoundContent}
              filterOption={(input, option: TAny) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={providerOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryProviderCell;
